import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Building2, CalendarDays, ClipboardCheck, Home, Inbox, LayoutDashboard, Menu, type LucideIcon } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useVerwaltungsMenue, isActivePath, VORSCHLAEGE } from './verwaltungsMenue';

/**
 * Navigation der Verwaltung auf dem Handy: vier feste Ziele und „Mehr" (das ganze Menü als
 * Schublade, `Sidebar`). Ersetzt den schwebenden Menüknopf, der über dem Seitentitel lag.
 *
 * Die vier Ziele sind das, was man unterwegs tut: nachsehen (Übersicht), prüfen (Vorschläge),
 * ein Event ändern, eine Anfrage beantworten. Zähler wie in der Seitenleiste; „Mehr" trägt die
 * Summe dessen, was dort offen ist und keinen eigenen Tab hat.
 */

interface Tab {
  path: string;
  label: string;
  icon: LucideIcon;
}

const TABS_REDAKTION: Tab[] = [
  { path: '/admin/start', label: 'Übersicht', icon: Home },
  { path: VORSCHLAEGE, label: 'Vorschläge', icon: ClipboardCheck },
  { path: '/events/manage', label: 'Events', icon: CalendarDays },
  { path: '/admin/anfragen', label: 'Anfragen', icon: Inbox },
];

const zahl = (n: number) => (n > 99 ? '99+' : String(n));

const VerwaltungTabLeiste: React.FC<{ mehrOffen: boolean; onMehr: () => void }> = ({ mehrOffen, onMehr }) => {
  const { pathname } = useLocation();
  const { isAdminOrModerator } = useAuth();
  const { sections, countFor, isOrganizer } = useVerwaltungsMenue();

  const tabs: Tab[] = isAdminOrModerator()
    ? TABS_REDAKTION
    : [
        { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
        ...(isOrganizer ? [{ path: '/veranstalter-bereich', label: 'Veranstalter', icon: Building2 }] : []),
      ];

  const tabPfade = new Set(tabs.map(t => t.path));
  const alleZiele = sections.flatMap(s => s.items.map(i => i.path));
  const mehrZaehler = alleZiele.filter(p => !tabPfade.has(p)).reduce((sum, p) => sum + countFor(p), 0);
  const tabAktiv = tabs.some(t => isActivePath(t.path, pathname));

  return (
    <nav className="bottom-tab-bar verwaltung-tabs" aria-label="Verwaltung">
      {tabs.map(({ path, label, icon: Icon }) => {
        const aktiv = !mehrOffen && isActivePath(path, pathname);
        const n = countFor(path);
        return (
          <Link key={path} to={path} className={`bottom-tab-item${aktiv ? ' active' : ''}`} aria-current={aktiv ? 'page' : undefined}>
            <span className="verwaltung-tabs__icon">
              <Icon size={22} strokeWidth={aktiv ? 2.2 : 1.9} aria-hidden />
              {n > 0 && <span className="verwaltung-tabs__badge" aria-label={`${n} offen`}>{zahl(n)}</span>}
            </span>
            <span className="bottom-tab-label">{label}</span>
          </Link>
        );
      })}
      <button
        type="button"
        className={`bottom-tab-item${mehrOffen || !tabAktiv ? ' active' : ''}`}
        onClick={onMehr}
        aria-expanded={mehrOffen}
        aria-label={mehrZaehler > 0 ? `Mehr, ${mehrZaehler} offen` : 'Mehr'}
      >
        <span className="verwaltung-tabs__icon">
          <Menu size={22} strokeWidth={mehrOffen || !tabAktiv ? 2.2 : 1.9} aria-hidden />
          {mehrZaehler > 0 && <span className="verwaltung-tabs__badge" aria-hidden>{zahl(mehrZaehler)}</span>}
        </span>
        <span className="bottom-tab-label">Mehr</span>
      </button>
    </nav>
  );
};

export default VerwaltungTabLeiste;
